// Global request throttle for Pollinations LLM API
// ALL LLM calls go through this single queue to prevent 429s.
// Images use a separate keyless endpoint with its own rate limit.

const MAX_CONCURRENT = 1
const MIN_GAP_MS = 3000  // 3s between requests — generous for public key limits
const MAX_429_RETRIES = 4
const BACKOFF_BASE_MS = 5000

let activeCount = 0
const queue: Array<{ run: () => void }> = []
let lastRequestTime = 0

function scheduleNext(): void {
  if (queue.length === 0 || activeCount >= MAX_CONCURRENT) return

  const now = Date.now()
  const timeSinceLast = now - lastRequestTime
  if (timeSinceLast < MIN_GAP_MS) {
    setTimeout(scheduleNext, MIN_GAP_MS - timeSinceLast)
    return
  }

  const next = queue.shift()!
  activeCount++
  lastRequestTime = Date.now()
  next.run()
}

function onComplete(): void {
  activeCount--
  scheduleNext()
}

async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  for (let attempt = 0; attempt <= MAX_429_RETRIES; attempt++) {
    const response = await fetch(input, init)
    if (response.status !== 429 || attempt === MAX_429_RETRIES) {
      return response
    }
    // Exponential backoff: 5s, 10s, 20s, 40s
    const delay = BACKOFF_BASE_MS * Math.pow(2, attempt)
    console.warn(`[throttle] 429 rate limit, backing off ${delay}ms (retry ${attempt + 1}/${MAX_429_RETRIES})`)
    await new Promise(resolve => setTimeout(resolve, delay))
    // Update lastRequestTime so the queue also waits
    lastRequestTime = Date.now()
  }
  // Unreachable, but TypeScript needs it
  return fetch(input, init)
}

/**
 * Throttled fetch — serializes ALL Pollinations LLM requests through a single
 * queue with 3s minimum gap and automatic 429 retry with exponential backoff.
 * Drop-in replacement for `fetch()` in API clients.
 */
export function throttledFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  return new Promise<Response>((resolve, reject) => {
    queue.push({
      run: () => {
        fetchWithRetry(input, init)
          .then(res => { onComplete(); resolve(res) })
          .catch(err => { onComplete(); reject(err) })
      },
    })
    scheduleNext()
  })
}
